import {
  MoreVert as MoreIcon,
  SettingsBackupRestore as ResetIcon,
} from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Checkbox,
  IconButton as MuiIconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { Box, Typography } from "@mui/material";
import Component from "components/Component";
import IconButton from "components/common/IconButton";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import * as labels from "config.labels";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as pricingActions from "store/actions/pricing/pricingActions";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import { v4 as uuidv4 } from "uuid";

const ActionMenu = ({ onResetSublimits }) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const [queryConfig] = staticDataService.useLazyConfigQuery(
    "submission_defaults"
  );
  const anchorRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const resetSublimits = async () => {
    const { data } = await queryConfig("submission_defaults");
    const defaultSublimits = data?.layerDefaults?.sublimit ?? [];
    onResetSublimits(defaultSublimits);
    setIsOpen(false);
  };

  return (
    <div className={"menu"}>
      <MuiIconButton
        ref={anchorRef}
        onClick={() => setIsOpen(!isOpen)}
        variant={"text"}
        className={"toggle"}
        style={{ marginLeft: "-15px" }}
      >
        <MoreIcon />
      </MuiIconButton>

      <Menu
        open={isOpen}
        anchorEl={anchorRef.current}
        onClose={(_, reason) => {
          if (reason === "backdropClick") setIsOpen(!isOpen);
        }}
      >
        <MenuItem onClick={resetSublimits}>
          <ListItemIcon>
            <ResetIcon />
          </ListItemIcon>
          <ListItemText>
            {translateLabel(labels.SUBLIMITS_RESET_TO_LATEST, {
              default: "Reset to the latest sublimits",
            })}
          </ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
};

const PercentageSublimits = () => {
  const state = useSelector((state) => state);
  const layer = pricingSelectors.getActiveLayer(state) ?? {};
  const dispatch = useDispatch();
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const productLine = useSelector(programSelectors.selectResolvedProgram)
    ?.productLine;

  const {
    data: generalConfiguration,
    isLoading: configLoading,
  } = staticDataService.useConfigQuery("general_configuration");

  const layerLimit = layer?.limit || 0;

  const displaySublimits = useMemo(() => {
    const allSublimits = layer?.sublimit || [];

    const hasBlankRow = allSublimits.some(
      (s) => !s.name.trim() && s.percentage === 0
    );

    if (hasBlankRow) {
      return allSublimits;
    } else {
      const blankSublimit = {
        id: `sublimit_${uuidv4()}`,
        name: "",
        percentage: 0,
        active: true,
        canDelete: true,
        canRename: true,
      };
      return [...allSublimits, blankSublimit];
    }
  }, [layer?.sublimit]);

  const updateLayer = useCallback(
    (key, value) => {
      if (!layer?.id) {
        return;
      }

      let finalValue = value;
      if (key === "sublimit") {
        finalValue = value.filter((s) => s.name.trim() || s.percentage > 0);
      }

      const values = {
        [key]: finalValue,
      };

      dispatch(
        pricingActions.updateLayer({
          layerId: layer.id,
          values,
        })
      );
    },
    [dispatch, layer?.id]
  );

  const deleteSublimit = (id) => {
    updateLayer(
      "sublimit",
      displaySublimits.filter((item) => item.id !== id)
    );
  };

  const updateSublimitName = (id, name) => {
    updateLayer(
      "sublimit",
      displaySublimits.map((item) => {
        if (item.id === id) {
          const { value, ...rest } = item;
          return {
            ...rest,
            name,
          };
        }
        return item;
      })
    );
  };

  const updateSublimitPercentage = (id, percentage) => {
    updateLayer(
      "sublimit",
      displaySublimits.map((item) => {
        if (item.id === id) {
          const { value, ...rest } = item;
          return {
            ...rest,
            percentage,
          };
        }
        return item;
      })
    );
  };

  const updateSublimitActive = (id, active) => {
    updateLayer(
      "sublimit",
      displaySublimits.map((item) => {
        if (item.id === id) {
          const { value, ...rest } = item;
          return {
            ...rest,
            active,
          };
        }
        return item;
      })
    );
  };

  const sublimitProductLines =
    generalConfiguration?.sublimit_product_lines ?? [];
  const shouldShowSublimits = sublimitProductLines.includes(productLine);

  if (!configLoading && !shouldShowSublimits) {
    return null;
  }

  if (configLoading) {
    return (
      <Component
        title={translateLabel(labels.PERCENTAGE_SUBLIMITS, {
          default: "Percentage Sublimits",
        })}
      >
        <Box display={"flex"} justifyContent={"center"} p={2}>
          <Typography>{"Loading..."}</Typography>
        </Box>
      </Component>
    );
  }

  const handleResetSublimits = (defaultSublimits) => {
    const currentSublimits = layer?.sublimit || [];

    const currentSublimitsById = new Map(
      currentSublimits.map((sublimit) => [sublimit.id, sublimit])
    );

    const defaultSublimitsById = new Map(
      defaultSublimits.map((sublimit) => [sublimit.id, sublimit])
    );

    const mergedSublimits = [];

    defaultSublimits.forEach((defaultSublimit) => {
      const existingCurrent = currentSublimitsById.get(defaultSublimit.id);

      if (existingCurrent) {
        mergedSublimits.push({
          ...defaultSublimit,
          percentage: existingCurrent.percentage,
          name: existingCurrent.name,
          active: existingCurrent.active ?? defaultSublimit.active ?? false,
        });
      } else {
        mergedSublimits.push({
          ...defaultSublimit,
          active: defaultSublimit.active ?? true,
        });
      }
    });

    currentSublimits.forEach((currentSublimit) => {
      if (!defaultSublimitsById.has(currentSublimit.id)) {
        mergedSublimits.push({
          ...currentSublimit,
          canDelete: true,
          canRename: true,
          orphaned: true,
        });
      }
    });

    updateLayer("sublimit", mergedSublimits);
  };

  return (
    <Component
      title={translateLabel(labels.PERCENTAGE_SUBLIMITS, {
        default: "Sublimits",
      })}
      options={<ActionMenu onResetSublimits={handleResetSublimits} />}
    >
      <TableContainer>
        <Table size={"small"}>
          <TableHead>
            <TableRow>
              <TableCell width={"80px"}>
                {translateLabel(labels.SUBLIMIT_ACTIVE, {
                  default: "Active",
                })}
              </TableCell>
              <TableCell>
                {translateLabel(labels.SUBLIMIT_NAME, { default: "Name" })}
              </TableCell>
              <TableCell width={"120px"}>
                {translateLabel(labels.SUBLIMIT_PERCENTAGE, {
                  default: "Percentage",
                })}
              </TableCell>
              <TableCell width={"150px"}>
                {translateLabel(labels.SUBLIMIT_VALUE, { default: "Value" })}
              </TableCell>
              <TableCell width={"50px"}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displaySublimits.map((sublimit, index) => {
              const isBlankRow =
                !sublimit.name.trim() && sublimit.percentage === 0;
              return (
                <TableRow
                  key={sublimit.id}
                  sx={{
                    backgroundColor:
                      sublimit.active && !isBlankRow
                        ? "action.selected"
                        : "transparent",
                    opacity: isBlankRow ? 0.7 : sublimit.active ? 1 : 0.6,
                    "&:hover": {
                      opacity: 1,
                    },
                  }}
                >
                  <TableCell sx={{ width: 80 }}>
                    <Checkbox
                      checked={sublimit.active}
                      onChange={(e) =>
                        updateSublimitActive(sublimit.id, e.target.checked)
                      }
                      disabled={isBlankRow}
                      size={"small"}
                    />
                  </TableCell>
                  <TableCell>
                    {sublimit.canRename === false ? (
                      <Typography variant={"body2"}>{sublimit.name}</Typography>
                    ) : (
                      <TextField
                        type={"text"}
                        variant={"filled"}
                        value={sublimit.name}
                        onChange={(e) =>
                          updateSublimitName(sublimit.id, e.target.value)
                        }
                        size={"small"}
                        fullWidth
                        hiddenLabel
                        name={sublimit.id}
                        placeholder={
                          isBlankRow
                            ? translateLabel(labels.SUBLIMIT_NAME, {
                                default: "Enter sublimit name...",
                              })
                            : ""
                        }
                        onFocus={(e) => {
                          if (!isBlankRow) {
                            setTimeout(() => e.target.select(), 0);
                          }
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ width: 120 }}>
                    <PrettyNumberTextField
                      type={"text"}
                      variant={"percentage"}
                      onChangeNumber={(percentage) =>
                        updateSublimitPercentage(sublimit.id, percentage)
                      }
                      value={sublimit.percentage}
                      size={"small"}
                      fullWidth
                      hiddenLabel
                      placeholder={isBlankRow ? "0%" : ""}
                      onFocus={(e) => {
                        if (!isBlankRow) {
                          e.target.select();
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: 150 }}>
                    <Typography
                      variant={"body2"}
                      sx={{
                        color: isBlankRow ? "text.secondary" : "text.primary",
                        fontWeight: sublimit.canRename === false ? 500 : 400,
                        padding: "8px 12px",
                      }}
                    >
                      {layerLimit
                        ? `$${(sublimit.value || 0).toLocaleString()}`
                        : "$0"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {sublimit.canDelete !== false && !isBlankRow && (
                      <IconButton
                        onClick={() => deleteSublimit(sublimit.id)}
                        size={"small"}
                        icon={DeleteIcon}
                        scale={"small"}
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Component>
  );
};

export default PercentageSublimits;
